import React, { useState, useMemo } from "react";
import {
  ToolShell, Field, UnitNumber, Select, Result,
  rainwaterYield, toBase, fromBase, altsFor, fmt,
  SpecCard, SpecValue,
} from "./_shared";

const ROOF_TYPES = [
  { value: "0.95", label: "Sheet metal roof (IBR / corrugated)" },
  { value: "0.90", label: "Concrete or slate tile" },
  { value: "0.85", label: "Cement / clay tile (default)" },
  { value: "0.70", label: "Thatch (high absorption losses)" },
  { value: "0.80", label: "Open paved area (driveway, courtyard)" },
];

export default function RainwaterCalculator() {
  const [areaVal, setAreaVal] = useState(200);
  const [areaUnit, setAreaUnit] = useState("m2");
  const [rainVal, setRainVal] = useState(650);
  const [rainUnit, setRainUnit] = useState("mm");
  const [coeff, setCoeff] = useState("0.85");
  const [ffVal, setFfVal] = useState(1.5);
  const [ffUnit, setFfUnit] = useState("mm");
  // Single rainfall event — for sizing the catch tank against one storm.
  const [eventVal, setEventVal] = useState(50);
  const [eventUnit, setEventUnit] = useState("mm");

  const area_m2 = toBase("area", areaVal, areaUnit);
  const rain_mm = toBase("rainfall", rainVal, rainUnit);
  const ff_mm = toBase("rainfall", ffVal, ffUnit);
  const event_mm = toBase("rainfall", eventVal, eventUnit);
  const c = parseFloat(coeff);

  const annualLitres = useMemo(() => {
    const gross = rainwaterYield({ area_m2, rain_mm, coeff: c });
    if (gross === null) return null;
    const ffLoss = area_m2 * ff_mm * 12;   // ~12 rain events / year
    return Math.max(0, gross - ffLoss);
  }, [area_m2, rain_mm, c, ff_mm]);

  // Per-event harvest = catchment area × event rainfall × runoff coefficient,
  // minus the first-flush diverter (one diversion per event).
  const eventLitres = useMemo(() => {
    if (!area_m2 || !event_mm) return null;
    const gross = area_m2 * event_mm * c;
    const ffLoss = area_m2 * ff_mm;
    return Math.max(0, gross - ffLoss);
  }, [area_m2, event_mm, c, ff_mm]);

  // Peak instantaneous flow during a heavy event — useful for sizing
  // gutters and downpipes.  Reported at a notional 1-hour storm duration.
  const eventFlowLpm = eventLitres !== null ? eventLitres / 60 : null;

  const dailyAvg = annualLitres !== null ? annualLitres / 365 : null;
  const monthlyAvg = annualLitres !== null ? annualLitres / 12 : null;

  return (
    <ToolShell
      slug="rainwater-yield"
      title="Rainwater harvest yield"
      oneLiner="Annual litres collectable from a roof or paved area given catchment, rainfall and runoff coefficient. First-flush losses deducted."
      onExport={() => ({
        inputs: [
          { label: "Catchment area", value: areaVal, unit: areaUnit === "m2" ? "m²" : areaUnit === "ft2" ? "ft²" : "ha" },
          { label: "Annual rainfall", value: rainVal, unit: rainUnit },
          { label: "Roof / catchment type", value: ROOF_TYPES.find((r) => r.value === coeff)?.label ?? coeff },
          { label: "First-flush diversion", value: ffVal, unit: ffUnit },
          { label: "Single rainfall event", value: eventVal, unit: eventUnit },
        ],
        results: [
          { label: "Annual harvest", value: fmt(annualLitres, 0), unit: "litres",
            alts: annualLitres !== null ? altsFor("volume", annualLitres, ["kL", "USgal"], 0) : null },
          { label: "Monthly average", value: fmt(monthlyAvg, 0), unit: "L / month",
            alts: monthlyAvg !== null ? [{ value: fmt(fromBase("volume", monthlyAvg, "kL"), 1), unit: "kL" }] : null },
          { label: "Daily average", value: fmt(dailyAvg, 0), unit: "L / day",
            note: "Average — actual yield is seasonal." },
          { label: `Per-event harvest (${fmt(event_mm, 0)} mm storm)`,
            value: fmt(eventLitres, 0), unit: "litres",
            alts: eventLitres !== null ? altsFor("volume", eventLitres, ["kL", "USgal"], 0) : null,
            note: "Size the catch tank to hold at least one full event without overflowing." },
          { label: "Peak inflow (1 h notional)", value: fmt(eventFlowLpm, 0), unit: "L / min",
            note: "Useful for sizing gutters and downpipes." },
        ],
        notes: [
          "Annual: Litres = area_m² × rainfall_mm × coefficient.  First-flush losses subtracted at ~12 rain events/year.",
          "Per event: Litres = area_m² × event_mm × coefficient − first-flush diversion.",
        ],
        spec: annualLitres !== null ? {
          title: "Rainwater yield specification",
          body: `A <strong>${fmt(area_m2, 0)} m²</strong> catchment receiving <strong>${rainVal} ${rainUnit}</strong> annual rainfall (runoff coefficient <strong>${c}</strong>) yields <strong>${fmt(annualLitres / 1000, 1)} kL/year</strong> after first-flush losses — averaging <strong>${fmt(dailyAvg, 0)} L/day</strong>. A <strong>${fmt(event_mm, 0)} mm</strong> storm delivers <strong>${fmt(eventLitres, 0)} L</strong> in one event (peak inflow <strong>${fmt(eventFlowLpm, 0)} L/min</strong>) — size the catch tank accordingly.`,
        } : null,
      })}
    >
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Catchment area">
          <UnitNumber dim="area" value={areaVal} onChange={setAreaVal} unit={areaUnit} onUnitChange={setAreaUnit}
            units={["m2", "ft2", "ha"]} testid="rw-area" />
        </Field>
        <Field label="Annual rainfall" hint="Long-term average for your site (your weather station or SAWS).">
          <UnitNumber dim="rainfall" value={rainVal} onChange={setRainVal} unit={rainUnit} onUnitChange={setRainUnit}
            units={["mm", "inch"]} testid="rw-rain" />
        </Field>
        <Field label="Roof / catchment type">
          <Select value={coeff} onChange={setCoeff} options={ROOF_TYPES} testid="rw-roof" />
        </Field>
        <Field label="First-flush diversion" hint="Volume diverted per m² per rain event. 1–2 mm typical.">
          <UnitNumber dim="rainfall" value={ffVal} onChange={setFfVal} unit={ffUnit} onUnitChange={setFfUnit}
            units={["mm", "inch"]} testid="rw-ff" />
        </Field>
        <Field label="Single rainfall event" span={2}
          hint="Test a specific storm size to sanity-check catch-tank capacity. 50 mm is a typical summer thunderstorm on the Highveld.">
          <UnitNumber dim="rainfall" value={eventVal} onChange={setEventVal} unit={eventUnit} onUnitChange={setEventUnit}
            units={["mm", "inch"]} testid="rw-event" />
        </Field>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <Result
          label="Annual harvest"
          primary={{ value: fmt(annualLitres, 0), unit: "litres" }}
          alts={annualLitres !== null ? altsFor("volume", annualLitres, ["kL", "USgal"], 0) : null}
          testid="rw-result-annual"
        />
        <Result
          label="Monthly average"
          primary={{ value: fmt(monthlyAvg, 0), unit: "L / month" }}
          alts={monthlyAvg !== null ? [{ value: fmt(fromBase("volume", monthlyAvg, "kL"), 1), unit: "kL" }] : null}
          testid="rw-result-monthly"
        />
        <Result
          label="Daily average"
          primary={{ value: fmt(dailyAvg, 0), unit: "L / day" }}
          note="Average — actual yield is seasonal."
          testid="rw-result-daily"
        />
      </div>

      {/* Per-event panel — sizing the catch tank for a single storm */}
      <div
        className="mt-5 p-5 md:p-6"
        style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.08)" }}
      >
        <div
          className="font-mono uppercase mb-3"
          style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.6)" }}
        >
          Single rainfall event · catch-tank sizing
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Result
            label={`Per-event harvest (${fmt(event_mm, 0)} mm)`}
            primary={{ value: fmt(eventLitres, 0), unit: "litres" }}
            alts={eventLitres !== null ? altsFor("volume", eventLitres, ["kL", "USgal"], 0) : null}
            note="Size the catch tank to hold at least one full event without overflowing."
            testid="rw-result-event"
          />
          <Result
            label="Peak inflow (1 h notional storm)"
            primary={{ value: fmt(eventFlowLpm, 0), unit: "L / min" }}
            note="Use to size gutter capacity and downpipe diameter."
            testid="rw-result-event-flow"
          />
        </div>
      </div>

      {annualLitres !== null && (
        <SpecCard
          eyebrow="Rainwater yield specification"
          testid="rw-spec-card"
        >
          A <SpecValue>{fmt(area_m2, 0)} m²</SpecValue> catchment receiving{" "}
          <SpecValue>{rainVal} {rainUnit}</SpecValue> annual rainfall (runoff coefficient{" "}
          <SpecValue>{c}</SpecValue>) yields{" "}
          <SpecValue>{fmt(annualLitres / 1000, 1)} kL/year</SpecValue> after first-flush losses — averaging{" "}
          <SpecValue>{fmt(dailyAvg, 0)} L/day</SpecValue>. A{" "}
          <SpecValue>{fmt(event_mm, 0)} mm</SpecValue> storm delivers{" "}
          <SpecValue>{fmt(eventLitres, 0)} L</SpecValue> in one event (peak inflow{" "}
          <SpecValue>{fmt(eventFlowLpm, 0)} L/min</SpecValue>) — size the catch tank accordingly.
        </SpecCard>
      )}
    </ToolShell>
  );
}
